import { z } from 'zod';
import { FIEL_SERIAL_LENGTH, isValidFielSerial, isValidMexicanPhone } from '../utils';
import { birthDateField } from './fields';

// ═══════════════════════════════════════════════════════════════
// ONBOARDING PM — Datos de identificación del propietario real (persona física)
// Diseño 2026-08-18. Va tras la estructura organizacional.
//
// El contrato de `POST …/beneficial-owners` sólo acepta name, rfc_or_curp,
// ownership_percentage, control_type, subject_type y relationship: NINGUNO de
// los datos de identificación de esta pantalla (género, nacimiento, domicilio,
// contacto, datos fiscales) tiene dónde guardarse todavía. Ver
// docs/qa/pm-documentos-listado-sesion-2026-08-17.md.
// ═══════════════════════════════════════════════════════════════

/** El país de nacimiento abre dos caminos: entidad federativa u "otro país". */
export const MEXICO = 'México' as const;
export const OTHER_COUNTRY = 'Otro' as const;

/**
 * Opciones locales de género. Son el FALLBACK de la pantalla cuando el catálogo
 * `genders` del backend no llega; el schema NO valida contra ellas —ver
 * `gender` abajo—.
 */
export const GENDERS = ['femenino', 'masculino', 'otro'] as const;
export type Gender = (typeof GENDERS)[number];

const requiredText = (message: string) => z.string().trim().min(1, message);

/**
 * ¿El país seleccionado es México? El catálogo `countries` del backend puede
 * codificarlo como `MX`, `MEX` o `México`, y el fallback local usa `MEXICO`.
 * Comparar contra la constante deja las reglas condicionales muertas en cuanto
 * el catálogo está disponible.
 */
export function isMexicoCountry(value: string | undefined): boolean {
  if (!value) return false;
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
  return normalized === 'mx' || normalized === 'mex' || normalized === 'mexico';
}

/**
 * Un propietario real persona física. Los campos del bloque fiscal son
 * OPCIONALES por diseño ("captúralos solo si el propietario dispone de ellos"),
 * así que ninguno bloquea el avance.
 */
export const beneficialOwnerIdentitySchema = z
  .object({
    // Identidad
    fullName: requiredText('El nombre completo es requerido'),
    // Texto requerido y NO un enum de `GENDERS`: el valor lo define el catálogo
    // `genders` del backend (`M`, `F`, …) y un enum local lo rechazaría, dejando
    // el campo en error para siempre aunque el usuario ya eligió.
    gender: requiredText('Selecciona el género'),
    // `birthDateField` valida fechas REALES (no acepta 31/02) y descarta
    // futuras: la misma pieza que usa el onboarding PF.
    birthDate: birthDateField({
      requiredMessage: 'La fecha de nacimiento es requerida',
      invalidMessage: 'Ingresa una fecha de nacimiento válida',
      futureMessage: 'La fecha de nacimiento no puede ser futura',
      minAge: 18,
      minAgeMessage: 'El propietario real debe ser mayor de edad',
    }),

    // Origen
    birthCountry: requiredText('Selecciona el país de nacimiento'),
    /** Sólo cuando el país de nacimiento es "Otro". */
    birthCountryOther: z.string().trim().optional(),
    /** Sólo cuando el país de nacimiento es México. */
    birthState: z.string().trim().optional(),
    nationality: requiredText('La nacionalidad es requerida'),

    // Contacto
    occupation: requiredText('La actividad económica es requerida'),
    address: requiredText('El domicilio es requerido'),
    /**
     * Al menos un teléfono; el diseño permite agregar varios. Cada renglón
     * capturado se valida a 10 dígitos — los vacíos se ignoran, porque agregar
     * un renglón y no llenarlo no es un error mientras haya otro válido.
     */
    phones: z
      .array(z.string().trim())
      .min(1)
      .superRefine((list, ctx) => {
        if (!list.some(p => p.length > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [0],
            message: 'Captura al menos un teléfono',
          });
          return;
        }
        list.forEach((phone, index) => {
          if (phone.length === 0) return;
          if (!isValidMexicanPhone(phone)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [index],
              message: 'El teléfono debe tener 10 dígitos',
            });
          }
        });
      }),
    email: z.union([z.literal(''), z.string().trim().email('Ingresa un correo válido')]).optional(),

    // Fiscal — todo opcional
    curp: z.string().trim().optional(),
    rfc: z.string().trim().optional(),
    taxIdNumber: z.string().trim().optional(),
    /** Sólo se pide cuando hay número de identificación fiscal. */
    taxIdCountry: z.string().trim().optional(),
    /**
     * Opcional, pero si se captura tiene que ser el serial real: 20 dígitos,
     * sin letras. Antes era texto libre y entraba cualquier cosa al expediente.
     */
    fielSerial: z
      .string()
      .trim()
      .refine(
        value => value.length === 0 || isValidFielSerial(value),
        `El número de serie de la e.firma debe tener ${FIEL_SERIAL_LENGTH} dígitos`
      )
      .optional(),

    // Residencia
    livesAbroad: z.boolean(),
    /**
     * Nullable a propósito, y es el único campo de la pantalla que lo es:
     * `null` significa "no contestó" y `false` "declaró que NO tiene domicilio
     * en México". Para el expediente no son lo mismo, así que un checkbox
     * —que no distingue sin tocar de desmarcado— no alcanzaba. Se captura con
     * el mismo par sí/no que `livesAbroad`.
     */
    hasMexicanAddress: z.boolean().nullable(),
    /** Sólo cuando reside en el extranjero y declara domicilio en México. */
    mexicanAddress: z.string().trim().optional(),
  })
  .superRefine((owner, ctx) => {
    // Los condicionales se validan aquí y no con campos requeridos sueltos:
    // dependen de otra respuesta, y exigirlos siempre bloquearía a quien no
    // aplica.
    if (owner.birthCountry === OTHER_COUNTRY && !owner.birthCountryOther) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['birthCountryOther'],
        message: 'Especifica el país de nacimiento',
      });
    }
    if (isMexicoCountry(owner.birthCountry) && !owner.birthState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['birthState'],
        message: 'Selecciona la entidad federativa',
      });
    }
    if (owner.taxIdNumber && !owner.taxIdCountry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['taxIdCountry'],
        message: 'Indica el país que asignó la identificación fiscal',
      });
    }
    if (owner.livesAbroad && owner.hasMexicanAddress && !owner.mexicanAddress) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mexicanAddress'],
        message: 'Captura el domicilio en México para correspondencia',
      });
    }
  });

export type BeneficialOwnerIdentity = z.infer<typeof beneficialOwnerIdentitySchema>;

/**
 * La pantalla captura una LISTA de propietarios y cierra con el nombre y la
 * firma del socio mayoritario. Ninguno de los dos lleva asterisco en el
 * diseño y aquí siguen opcionales —un POST con sólo uno de los dos sigue
 * siendo válido—, pero la pantalla exige LOS DOS para dejar avanzar: con
 * cualquiera de los dos vacío, Continuar se bloquea. Esa regla vive en
 * `BeneficialOwnerIdentityForm` (gate de CONTINUAR), no aquí, porque la firma
 * no es un campo de react-hook-form —vive en un `SignaturePadRef`— y este
 * schema no la conoce.
 */
export const beneficialOwnersIdentitySchema = z.object({
  owners: z.array(beneficialOwnerIdentitySchema).min(1, 'Declara al menos un propietario real'),
  majorityPartnerName: z.string().trim().optional(),
});

export type BeneficialOwnersIdentityFormData = z.infer<typeof beneficialOwnersIdentitySchema>;

/**
 * Cuenta los campos obligatorios ya capturados de un propietario, para el
 * resumen "x de N completados" del encabezado. La entidad federativa y el país
 * "otro" son excluyentes: sólo cuenta el que aplica según dónde nació.
 */
export function countBeneficialOwnerProgress(owner: BeneficialOwnerIdentity): {
  done: number;
  total: number;
} {
  const base = [
    owner.fullName,
    owner.gender,
    owner.birthDate,
    owner.birthCountry,
    owner.nationality,
    owner.occupation,
    owner.address,
    owner.phones.find(p => p.trim().length > 0) ?? '',
  ];
  const conditional =
    owner.birthCountry === MEXICO
      ? owner.birthState
      : owner.birthCountry === OTHER_COUNTRY
        ? owner.birthCountryOther
        : null;

  const fields = conditional === null ? base : [...base, conditional];
  return {
    done: fields.filter(value => (value ?? '').toString().trim().length > 0).length,
    total: fields.length,
  };
}

/** Propietario vacío: el estado inicial de la pantalla y de cada "Agregar". */
export function emptyBeneficialOwnerIdentity(): BeneficialOwnerIdentity {
  return {
    fullName: '',
    gender: '' as Gender,
    birthDate: '',
    birthCountry: MEXICO,
    birthCountryOther: '',
    birthState: '',
    nationality: '',
    occupation: '',
    address: '',
    phones: [''],
    email: '',
    curp: '',
    rfc: '',
    taxIdNumber: '',
    taxIdCountry: '',
    fielSerial: '',
    livesAbroad: false,
    // `null` = sin contestar. Nace así para no declarar por el usuario que no
    // tiene domicilio en México cuando en realidad nadie se lo preguntó.
    hasMexicanAddress: null,
    mexicanAddress: '',
  };
}
